import {
  Carousel,
  CarouselControls,
  CarouselNavigation,
  CarouselPagination,
  CarouselSlide,
} from '@cinch-labs/design-system'
import { TrackingEvent, useAnalytics } from '@cinch-nx/shared-analytics'
import cx from 'classnames'
import React, {
  isValidElement,
  ReactElement,
  ReactNode,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react'
import Swiper from 'swiper'

import styles from './looping-carousel.module.css'

export interface SwiperContainer extends HTMLElement {
  swiper?: Swiper
}

interface LoopingCarouselProps {
  children: ReactNode
  slideSpeedInSeconds?: number
  shouldLoop?: boolean
  className?: {
    container?: string
    carousel?: string
    carouselSlide?: string
    controlsWrapper?: string
    customPagination?: string
  }
}

const useAutoSlide = (
  containerRef: React.RefObject<HTMLDivElement | null>,
  {
    shouldLoop,
    slideSpeedInSeconds,
    isPaused,
  }: {
    shouldLoop: boolean
    slideSpeedInSeconds: number
    isPaused: boolean
  },
) => {
  useEffect(() => {
    if (!shouldLoop || isPaused) return

    const interval = setInterval(() => {
      const swiperContainer = containerRef.current?.querySelector(
        '.swiper',
      ) as SwiperContainer
      swiperContainer?.swiper?.slideNext()
    }, slideSpeedInSeconds * 1000)

    return () => clearInterval(interval)
  }, [shouldLoop, slideSpeedInSeconds, isPaused, containerRef])
}

export default function LoopingCarousel({
  children,
  slideSpeedInSeconds = 2,
  shouldLoop = true,
  className,
}: LoopingCarouselProps): ReactElement | null {
  const reactId = useId()
  const carouselId = useMemo(
    () => `carousel-${reactId.replace(/:/g, '')}`,
    [reactId],
  )
  const containerRef = useRef<HTMLDivElement>(null)
  const [isPaused, setIsPaused] = useState(false)

  useAutoSlide(containerRef, {
    shouldLoop,
    slideSpeedInSeconds,
    isPaused,
  })

  if (!children || React.Children.count(children) === 0) {
    return null
  }

  return (
    <div
      ref={containerRef}
      className={className?.container}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      onFocus={() => setIsPaused(true)}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) {
          setIsPaused(false)
        }
      }}
    >
      <Carousel
        name={carouselId}
        customClassNames={{
          carousel: cx(className?.carousel, styles.carousel),
        }}
        swiperSettings={{
          slidesPerView: 1,
          loop: shouldLoop,
          speed: 600,
        }}
      >
        {React.Children.map(children, (child, index) => (
          <CarouselSlide
            key={isValidElement(child) && child.key != null ? child.key : index}
            className={cx(className?.carouselSlide, styles.carouselSlide)}
          >
            {child}
          </CarouselSlide>
        ))}
      </Carousel>

      <CarouselControls customClassNames={className?.controlsWrapper}>
        <CarouselPagination
          name={carouselId}
          customClassNames={className?.customPagination}
        />
        <CarouselNavigation
          name={carouselId}
          ariaLabels={{
            previous: 'Go to previous slide',
            next: 'Go to next slide',
          }}
        />
      </CarouselControls>
    </div>
  )
}

export interface SlideTrackingEvents {
  onClick?: TrackingEvent
}

interface SlideProps {
  children: ReactNode
  trackingEvents?: SlideTrackingEvents
  ariaLabel?: string
  onClick?: () => void
}

function Slide({
  children,
  trackingEvents,
  ariaLabel,
  onClick,
}: SlideProps): ReactElement {
  const { trackEvent } = useAnalytics()

  const handleOnClick = () => {
    if (trackingEvents?.onClick) {
      trackEvent(trackingEvents.onClick)
    }

    onClick?.()
  }

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.target !== event.currentTarget) {
      return
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      handleOnClick()
    }
  }

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={ariaLabel}
      onClick={handleOnClick}
      onKeyDown={handleKeyDown}
    >
      {children}
    </div>
  )
}

LoopingCarousel.Slide = Slide
